import React, { useMemo, useState } from 'react';
import {
  Combobox,
  type ComboboxOption,
  type ComboboxPlainOption,
  FormGroup,
  FormLabel,
  SelectBoxSkeleton,
} from '@sbt-web/ui';
import { useViewport } from '@sbt-web/hooks';
import NewBadge from '../NewBadge';
import { FilterURI, type ListFilter } from '@sbt-web/network/types';
import {
  comboboxOptionMapper,
  useValueList,
  ValueListStatus,
} from '@client/hooks/useValueList';
import { Category } from '@sbt-web/category-utils';
import { useDataSource } from '@client/hooks/useDataSource';

import classNames from 'classnames';
import commonClasses from '../SelectorStyles/common.module.scss';

const filterableSelectorEnabledFilterURIs: string[] = [
  FilterURI.CarBrand,
  FilterURI.BikeBrand,
  FilterURI.MarketClothingBrand,
  FilterURI.MarketClothingSize,
] as const;

export const shouldUseFilterableListSelector = (filterURI: string) =>
  filterableSelectorEnabledFilterURIs.includes(filterURI);

type Props = {
  filter: ListFilter;
  category?: Category;
  onSelect: (key: string, value?: string, label?: string) => void;
  initialValue?: string;
  dependenciesValues?: { [key: string]: string };
};

const NEW_FILTERS = new Set<string>([
  FilterURI.MarketClothingBrand,
  FilterURI.MarketClothingSize,
]);

const FilterableListSelector = ({
  filter,
  category,
  onSelect,
  initialValue,
  dependenciesValues,
}: Props) => {
  // Hide most frequent options when searching
  const [hideMostFrequent, setHideMostFrequent] = useState(false);
  const { isMobile } = useViewport();

  const { dataSource, dependenciesFilled } = useDataSource(
    filter.dataSource,
    filter.dependencies,
    dependenciesValues
  );

  const valueListData = useValueList(
    dataSource,
    category?.id,
    !dependenciesFilled
  );

  const { value: valueList, state: valueListState } = valueListData;

  const plainOptions = useMemo<ComboboxPlainOption[]>(
    () => valueList?.values?.map(comboboxOptionMapper) ?? [],
    [valueList]
  );

  const groupedOptions = useMemo<ComboboxOption[]>(() => {
    if (!valueList?.topValues) return plainOptions;
    return [
      {
        label: 'Più frequenti',
        options: valueList.topValues.map(comboboxOptionMapper),
      },
      {
        label: 'Tutti',
        options: plainOptions,
      },
    ];
  }, [plainOptions, valueList]);

  const options = hideMostFrequent ? plainOptions : groupedOptions;

  const handleOnSingleValueChange = (value?: string) => {
    const label = valueList?.values?.find((v) => v.key === value)?.value;
    onSelect(filter.queryString, value, label);
    setHideMostFrequent(false);
  };

  const handleOnMultipleValueChange = (value?: string[]) => {
    const label = valueList?.values
      ?.filter((v) => value?.includes(v.key))
      .map((v) => v.value)
      .join(',');
    const concatValue = value?.join(',');
    onSelect(filter.queryString, concatValue, label);
    setHideMostFrequent(false);
  };

  const handleOnSearch = (input?: string) => {
    setHideMostFrequent(!!input);
  };

  const showNewBadge = NEW_FILTERS.has(filter.uri);

  if (valueListState === ValueListStatus.ERROR) return;

  return (
    <FormGroup>
      <div
        className={classNames(
          commonClasses['filler-div'],
          commonClasses['label-with-extra']
        )}
      >
        <FormLabel>{filter.label}</FormLabel>
        {showNewBadge && <NewBadge />}
      </div>
      {valueListState === ValueListStatus.LOADING ? (
        <SelectBoxSkeleton
          expanded={filter.multiSelection}
          flavor={isMobile ? 'large' : 'small'}
        />
      ) : (
        <div>
          {filter.multiSelection ? (
            <Combobox
              ariaLabel={filter.label}
              options={options}
              value={initialValue?.split(',') ?? []}
              onValueChange={handleOnMultipleValueChange}
              onSearch={handleOnSearch}
              size={isMobile ? 'lg' : 'sm'}
              placeholder="Qualsiasi"
              multiple
            />
          ) : (
            <Combobox
              ariaLabel={filter.label}
              options={options}
              value={initialValue}
              onValueChange={handleOnSingleValueChange}
              onSearch={handleOnSearch}
              size={isMobile ? 'lg' : 'sm'}
              placeholder="Qualsiasi"
            />
          )}
        </div>
      )}
    </FormGroup>
  );
};

export { FilterableListSelector };
